import React from 'react';
import {
  GetEventAccountDocument,
  GetEventAccountQuery,
} from '../../../../graphql/queries/getEventAccount.generated';
import { Link, useHistory } from 'react-router-dom';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useCreateLocationForEventAccountMutation } from '../../../../graphql/queries/createLocationForEventAccount.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import { FaLink, FaPlus } from 'react-icons/fa';
import ItemLocationSearch from '../../../../components/ItemLocationSearch';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { useLinkItemLocationToEventAccountMutation } from '../../../../graphql/mutations/linkItemLocationToEventAccount.generated';
import { Routes } from '@/utils/routes';
import { useAllEventsPaginatedQuery } from '../../../../graphql/queries/allEventsPaginated.generated';
import { useUpdateItemLocationMutation } from '../../../../graphql/mutations/updateItemLocation.generated';
import i18n from '@/utils/i18n';

export function ItemLocationTable(props: {
  eventAccount: GetEventAccountQuery['eventAccount'];
}) {
  const { eventAccount } = props;

  const history = useHistory();

  const [showLinkItemLocationModal, setShowLinkItemLocationModal] =
    React.useState(false);
  const [selectedItemLocationId, setSelectedItemLocationId] =
    React.useState('');
  const [selectedItemLocationName, setSelectedItemLocationName] =
    React.useState('');
  const [selectedEventId, setSelectedEventId] = React.useState('');

  const [showCreateItemLocationModal, setShowCreateItemLocationModal] =
    React.useState(false);
  const [selectedCreateEventId, setSelectedCreateEventId] = React.useState('');

  const [createLocation, { loading: creatingLocation }] =
    useCreateLocationForEventAccountMutation();

  const [linkItemLocationToEventAccount, { loading: linkingItemLocation }] =
    useLinkItemLocationToEventAccountMutation({});

  const [updateItemLocation, { loading: updatingItemLocation }] =
    useUpdateItemLocationMutation();

  const { data: eventsData, loading: loadingEvents } =
    useAllEventsPaginatedQuery({
      variables: {
        filter: { event_account_id: eventAccount?.id || '' },
        skip: 0,
        take: 1000,
      },
      skip: !eventAccount?.id,
      fetchPolicy: 'cache-and-network',
    });

  const selectableEvents =
    eventsData?.eventsPaginated?.events
      ?.filter((e) => e?.id)
      ?.map((e) => ({ id: e.id as string, name: e.name || e.id })) || [];

  function getAuthorizedSignerNames(itemLocationId: string) {
    return eventAccount?.authorized_signers
      ?.filter((as) =>
        as.allowed_item_locations?.some((loc) => itemLocationId === loc.id),
      )
      .map((as) => as.name)
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
  }

  const handleCreateLocation = async () => {
    if (eventAccount == null) return;
    try {
      const result = await createLocation({
        variables: {
          name: 'New ' + eventAccount?.name + ' location',
          event_account_id: eventAccount.id,
          type: 'Event',
        },
        refetchQueries: [
          {
            query: GetEventAccountDocument,
            variables: { id: eventAccount.id },
          },
        ],
      });

      const createdId = result?.data?.createItemLocation?.id;
      if (!createdId) {
        throw new Error('CREATE_ITEM_LOCATION_FAILED');
      }

      // Set event on the newly created item location
      if (!selectedCreateEventId) {
        throw new Error('EVENT_NOT_SELECTED');
      }

      await updateItemLocation({
        variables: {
          id: createdId,
          name: result.data!.createItemLocation.name,
          event_id: selectedCreateEventId,
        },
        refetchQueries: [
          {
            query: GetEventAccountDocument,
            variables: { id: eventAccount.id },
          },
        ],
      });

      history.push(Routes.itemLocation(createdId));
    } catch (e: any) {
      LayoutStoreInstance.setError(e?.message || 'Error creating location');
    }
  };

  return (
    <div>
      <h4>Item locations</h4>
      <div className="text-end">
        <Button
          className="mt-2 mb-2"
          disabled={linkingItemLocation}
          onClick={() => setShowLinkItemLocationModal(true)}
        >
          <FaLink /> {'Link item location'}
        </Button>
      </div>
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Allowed Authorized Signers</th>
            <th>Assigned event</th>
          </tr>
        </thead>
        <tbody>
          {[...(eventAccount?.item_locations || [])]
            ?.sort((a, b) => a.name.localeCompare(b.name))
            .map((loc) => (
              <tr key={`${eventAccount?.id}-${loc.id}`}>
                <td>
                  <Link to={'/item-location/' + loc.id}>{loc.name}</Link>
                </td>
                <td>{getAuthorizedSignerNames(loc.id)}</td>
                <td>
                  <Link to={'/event/' + loc.event?.id}>
                    {loc.event?.name || loc.event?.id || ''}
                  </Link>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      <Button
        className="mb-3"
        color="primary"
        onClick={() => setShowCreateItemLocationModal(true)}
        disabled={creatingLocation}
      >
        <FaPlus /> {i18n.t('EventAccountSettings:addItemLocation')}
      </Button>
      <Modal
        isOpen={showLinkItemLocationModal}
        onClose={() => setShowLinkItemLocationModal(false)}
      >
        <ModalHeader>Link item location to event account</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Select Event</Label>
            <Input
              type="select"
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.currentTarget.value)}
              disabled={loadingEvents}
            >
              <option value="">Select</option>
              {selectableEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <ItemLocationSearch
            setLocationInfo={(id, name) => {
              setSelectedItemLocationId(id);
              setSelectedItemLocationName(name);
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            className="mt-3"
            onClick={() => setShowLinkItemLocationModal(false)}
            color="danger"
          >
            Cancel
          </Button>
          <Button
            className="mt-3"
            disabled={
              isNullOrWhitespace(selectedItemLocationId) ||
              isNullOrWhitespace(selectedEventId)
            }
            onClick={async () => {
              if (eventAccount != null) {
                try {
                  await linkItemLocationToEventAccount({
                    variables: {
                      event_account_id: eventAccount.id,
                      item_location_id: selectedItemLocationId,
                    },
                    refetchQueries: [
                      {
                        query: GetEventAccountDocument,
                        variables: { id: eventAccount.id },
                      },
                    ],
                  });

                  if (!selectedItemLocationName) {
                    throw new Error('ITEM_LOCATION_NAME_MISSING');
                  }

                  await updateItemLocation({
                    variables: {
                      id: selectedItemLocationId,
                      name: selectedItemLocationName,
                      event_id: selectedEventId,
                    },
                    refetchQueries: [
                      {
                        query: GetEventAccountDocument,
                        variables: { id: eventAccount.id },
                      },
                    ],
                  });

                  setShowLinkItemLocationModal(false);
                  setSelectedItemLocationId('');
                  setSelectedEventId('');
                  setSelectedItemLocationName('');
                } catch (e: any) {
                  LayoutStoreInstance.setError(
                    e?.message || 'Error linking item location',
                  );
                }
              }
            }}
          >
            Link
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showCreateItemLocationModal}
        onClose={() => setShowCreateItemLocationModal(false)}
      >
        <ModalHeader>Create item location</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Select Event</Label>
            <Input
              type="select"
              value={selectedCreateEventId}
              onChange={(e) => setSelectedCreateEventId(e.currentTarget.value)}
              disabled={loadingEvents}
            >
              <option value="">Select</option>
              {selectableEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            className="mt-3"
            onClick={() => setShowCreateItemLocationModal(false)}
            color="danger"
          >
            Cancel
          </Button>
          <Button
            className="mt-3"
            disabled={
              isNullOrWhitespace(selectedCreateEventId) || loadingEvents
            }
            onClick={async () => {
              await handleCreateLocation();
              setShowCreateItemLocationModal(false);
              setSelectedCreateEventId('');
            }}
            color="primary"
          >
            Create
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
